@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');

@tailwind base;
@tailwind components;
@tailwind utilities;

:root {
  /* Background – warm cream from the interior glow */
  --background-50: 0.973 0.008 82;
  --background-100: 0.955 0.011 82;
  --background-200: 0.925 0.015 80;
  --background-300: 0.888 0.019 78;
  --background-400: 0.845 0.023 76;
  --background-500: 0.790 0.027 74;
  --background-600: 0.690 0.027 72;
  --background-700: 0.575 0.025 70;
  --background-800: 0.460 0.021 68;
  --background-900: 0.352 0.017 66;
  --background-950: 0.255 0.013 64;

  /* Primary – near-black charcoal facade */
  --primary-50: 0.968 0.004 160;
  --primary-100: 0.928 0.005 160;
  --primary-200: 0.868 0.006 160;
  --primary-300: 0.768 0.007 160;
  --primary-400: 0.470 0.008 160;
  --primary-500: 0.305 0.007 160;
  --primary-600: 0.252 0.006 160;
  --primary-700: 0.218 0.006 160;
  --primary-800: 0.188 0.005 160;
  --primary-900: 0.162 0.005 160;
  --primary-950: 0.120 0.004 160;

  /* Accent – warm wood and fairy-light gold */
  --accent-50: 0.978 0.020 76;
  --accent-100: 0.955 0.038 76;
  --accent-200: 0.922 0.056 76;
  --accent-300: 0.878 0.072 76;
  --accent-400: 0.832 0.082 76;
  --accent-500: 0.785 0.084 76;
  --accent-600: 0.722 0.080 74;
  --accent-700: 0.640 0.072 72;
  --accent-800: 0.545 0.060 70;
  --accent-900: 0.442 0.048 68;
  --accent-950: 0.320 0.036 66;

  /* Secondary – red from the STANKA sign */
  --secondary-50: 0.965 0.018 30;
  --secondary-100: 0.930 0.038 30;
  --secondary-200: 0.878 0.068 29;
  --secondary-300: 0.800 0.120 29;
  --secondary-400: 0.660 0.175 29;
  --secondary-500: 0.520 0.195 28.5;
  --secondary-600: 0.470 0.180 28;
  --secondary-700: 0.410 0.160 27;
  --secondary-800: 0.340 0.130 26;
  --secondary-900: 0.270 0.100 25;
  --secondary-950: 0.185 0.072 24;

  /* Foreground – neutral charcoal text */
  --foreground-50: 0.975 0.004 160;
  --foreground-100: 0.945 0.005 160;
  --foreground-200: 0.890 0.006 160;
  --foreground-300: 0.800 0.007 160;
  --foreground-400: 0.650 0.008 160;
  --foreground-500: 0.520 0.008 160;
  --foreground-600: 0.420 0.008 160;
  --foreground-700: 0.340 0.007 160;
  --foreground-800: 0.265 0.006 160;
  --foreground-900: 0.220 0.006 160;
  --foreground-950: 0.170 0.006 160;

  --font-heading: 'Playfair Display', Georgia, 'Times New Roman', serif;
  --font-body: 'Manrope', ui-sans-serif, system-ui, sans-serif;
  --font-label: 'Manrope', ui-sans-serif, system-ui, sans-serif;

  --nav-height: 84px;
}

@layer base {
  html {
    scroll-behavior: smooth;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-text-size-adjust: 100%;
  }

  body {
    background-color: oklch(var(--background-100));
    color: oklch(var(--foreground-900));
    font-family: var(--font-body);
    font-weight: 400;
    line-height: 1.7;
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-heading);
    font-weight: 500;
    line-height: 1.1;
  }

  ::selection {
    background-color: oklch(var(--accent-500));
    color: oklch(var(--primary-950));
  }

  button, a {
    -webkit-tap-highlight-color: transparent;
  }
}

@layer components {
  .section-shell {
    width: 100%;
    max-width: 1240px;
    margin-left: auto;
    margin-right: auto;
    padding-left: 20px;
    padding-right: 20px;
  }

  @media (min-width: 768px) {
    .section-shell {
      padding-left: 32px;
      padding-right: 32px;
    }
  }

  @media (min-width: 1024px) {
    .section-shell {
      padding-left: 48px;
      padding-right: 48px;
    }
  }

  .headline {
    font-family: var(--font-heading);
    font-weight: 500;
    line-height: 1.06;
    letter-spacing: -0.012em;
    text-wrap: balance;
  }

  .lead {
    font-size: 14.5px;
    line-height: 1.9;
    color: oklch(var(--foreground-700));
  }

  @media (min-width: 768px) {
    .lead {
      font-size: 15px;
    }
  }

  .hairline {
    display: block;
    width: 100%;
    height: 1px;
    background-color: oklch(var(--background-300));
  }

  .card-lift {
    transition: transform 0.6s cubic-bezier(0.22, 0.7, 0.2, 1),
      border-color 0.5s ease, background-color 0.5s ease;
  }

  .card-lift:hover {
    transform: translateY(-4px);
  }

  .diamond-rule {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .diamond-rule::before,
  .diamond-rule::after {
    content: "";
    display: block;
    height: 1px;
    width: 36px;
    background-color: oklch(var(--accent-600) / 0.45);
  }

  .diamond-rule > span {
    display: block;
    width: 5px;
    height: 5px;
    transform: rotate(45deg);
    background-color: oklch(var(--accent-500));
  }

  .eyebrow {
    font-family: var(--font-label);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
  }

  .rule-line {
    display: inline-block;
    width: 56px;
    height: 1px;
    background-color: oklch(var(--accent-600) / 0.7);
  }

  .btn-base {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: var(--font-label);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    white-space: nowrap;
    padding: 15px 30px;
    border-radius: 6px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background-color 0.4s ease, color 0.4s ease, border-color 0.4s ease, transform 0.4s ease;
  }

  .btn-base:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 640px) {
    .btn-base {
      padding: 14px 22px;
      font-size: 12px;
      letter-spacing: 0.08em;
    }
  }
}

@layer utilities {
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
}

/* Brand neon wordmark */
.brand-neon-red {
  color: oklch(var(--secondary-500));
  text-shadow: 0 0 8px oklch(var(--secondary-500) / 0.28), 0 0 18px oklch(var(--secondary-600) / 0.14);
}

.brand-neon-red-bright {
  color: oklch(var(--secondary-400));
  text-shadow: 0 0 4px oklch(var(--secondary-200) / 0.65),
    0 0 12px oklch(var(--secondary-500) / 0.5),
    0 0 24px oklch(var(--secondary-600) / 0.28);
}

.brand-neon-white {
  color: oklch(var(--foreground-700));
}

.brand-neon-white-bright {
  color: oklch(var(--background-50));
  text-shadow: 0 0 5px oklch(var(--background-50) / 0.7),
    0 0 14px oklch(var(--foreground-200) / 0.4);
}

/* Scroll reveal */
.reveal {
  opacity: 0;
  transform: translateY(26px);
  filter: blur(5px);
  transition: opacity 0.75s cubic-bezier(0.22, 0.7, 0.2, 1),
    transform 0.75s cubic-bezier(0.22, 0.7, 0.2, 1),
    filter 0.75s cubic-bezier(0.22, 0.7, 0.2, 1);
  will-change: opacity, transform;
}

.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
  /* "none" removes the filter layer entirely once the animation is done,
     instead of keeping a blur(0) compositing layer on every element */
  filter: none;
  will-change: auto;
}

.reveal-soft {
  opacity: 0;
  transition: opacity 0.9s cubic-bezier(0.22, 0.7, 0.2, 1);
}

.reveal-soft.is-visible {
  opacity: 1;
}

/* Menu list items – gentle stagger when a category opens */
.menu-item {
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(0.22, 0.7, 0.2, 1);
}

.is-open .menu-item {
  opacity: 1;
  transform: translateY(0);
}

/* Hero intro animations */
@keyframes riseIn {
  from {
    opacity: 0;
    transform: translateY(34px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slowZoom {
  from { transform: scale(1.08); }
  to { transform: scale(1); }
}

@keyframes scrollPulse {
  0% { transform: translateY(0); opacity: 0.9; }
  50% { transform: translateY(9px); opacity: 0.4; }
  100% { transform: translateY(0); opacity: 0.9; }
}

.anim-rise {
  animation: riseIn 1.1s cubic-bezier(0.22, 0.7, 0.2, 1) both;
}

.anim-fade {
  animation: fadeIn 1.6s ease both;
}

.anim-zoom {
  animation: slowZoom 14s cubic-bezier(0.22, 0.7, 0.2, 1) both;
}

.scroll-dot {
  animation: scrollPulse 2.4s ease-in-out infinite;
}

.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }
.delay-300 { animation-delay: 0.3s; }
.delay-400 { animation-delay: 0.4s; }
.delay-500 { animation-delay: 0.5s; }
.delay-600 { animation-delay: 0.6s; }
.delay-800 { animation-delay: 0.8s; }

/* Scrollbar */
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: oklch(var(--background-200));
}

::-webkit-scrollbar-thumb {
  background: oklch(var(--primary-400));
  border-radius: 999px;
}

::-webkit-scrollbar-thumb:hover {
  background: oklch(var(--primary-500));
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
  .reveal,
  .reveal-soft {
    opacity: 1;
    transform: none;
    transition: none;
  }
  .anim-rise,
  .anim-fade,
  .anim-zoom,
  .scroll-dot {
    animation: none;
  }
}